import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { Navbar } from "@/components/Navbar";
import { MovieCard, MovieCardSkeleton } from "@/components/MovieCard";
import { API } from "@/App";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Film, X, User, Clapperboard } from "lucide-react";

export const MovieSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [actorFilter, setActorFilter] = useState(searchParams.get("actor") || "");
  const [directorFilter, setDirectorFilter] = useState(searchParams.get("director") || "");

  const { ref: sentinelRef, inView } = useInView({
    threshold: 0,
    rootMargin: "100px"
  });

  const fetchMovies = useCallback(async (skip = 0, append = false) => {
    try {
      if (!append) setLoading(true);
      else setLoadingMore(true);

      const params = new URLSearchParams();
      params.set("skip", skip.toString());
      params.set("limit", "10");
      
      if (searchQuery) params.set("search", searchQuery);
      if (actorFilter) params.set("actor", actorFilter);
      if (directorFilter) params.set("director", directorFilter);

      const response = await axios.get(`${API}/movies?${params.toString()}`);
      const data = response.data;

      if (append) {
        setMovies((prev) => [...prev, ...data.movies]);
      } else {
        setMovies(data.movies);
      }

      setTotal(data.total);
      setHasMore(data.has_more);
    } catch (error) {
      console.error("Error fetching movies:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchQuery, actorFilter, directorFilter]);

  // Initial fetch
  useEffect(() => {
    fetchMovies(0, false);
  }, [fetchMovies]);

  // Infinite scroll
  useEffect(() => {
    if (inView && hasMore && !loading && !loadingMore) {
      fetchMovies(movies.length, true);
    }
  }, [inView, hasMore, loading, loadingMore, movies.length, fetchMovies]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set("search", searchQuery);
    if (actorFilter) params.set("actor", actorFilter);
    if (directorFilter) params.set("director", directorFilter);
    setSearchParams(params);
    setMovies([]);
    fetchMovies(0, false);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setActorFilter("");
    setDirectorFilter("");
    setSearchParams({});
    setMovies([]);
    fetchMovies(0, false);
  };

  const hasFilters = searchQuery || actorFilter || directorFilter;

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="movie-search">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mb-2">
              Rechercher des films
            </h1>
            <p className="text-gray-400">
              Explorez notre bibliothèque de {total} film{total !== 1 ? "s" : ""}
            </p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="mb-8">
            <div className="glass rounded-xl p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Title Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="text"
                    placeholder="Titre du film..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-black/30 border-white/10 focus:border-violet-500 search-glow"
                    data-testid="search-title"
                  />
                </div>

                {/* Actor Filter */}
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="text"
                    placeholder="Acteur..."
                    value={actorFilter}
                    onChange={(e) => setActorFilter(e.target.value)}
                    className="pl-10 bg-black/30 border-white/10 focus:border-violet-500"
                    data-testid="search-actor"
                  />
                </div>

                {/* Director Filter */}
                <div className="relative">
                  <Clapperboard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="text"
                    placeholder="Réalisateur..."
                    value={directorFilter}
                    onChange={(e) => setDirectorFilter(e.target.value)}
                    className="pl-10 bg-black/30 border-white/10 focus:border-violet-500"
                    data-testid="search-director"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  className="bg-violet-600 hover:bg-violet-500"
                  data-testid="search-submit"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Rechercher
                </Button>
                {hasFilters && (
                  <Button
                    type="button"
                    variant="outline"
                    className="border-white/10 hover:bg-white/5"
                    onClick={clearFilters}
                    data-testid="search-clear"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Effacer
                  </Button>
                )}
              </div>
            </div>
          </form>

          {/* Results */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
              {[...Array(10)].map((_, i) => (
                <MovieCardSkeleton key={i} />
              ))}
            </div>
          ) : movies.length > 0 ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
                {movies.map((movie) => (
                  <MovieCard key={movie.movie_id} movie={movie} />
                ))}
              </div>

              {/* Infinite Scroll Sentinel */}
              {hasMore && (
                <div ref={sentinelRef} className="scroll-sentinel mt-8">
                  {loadingMore && (
                    <div className="flex justify-center py-8">
                      <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <Film className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-400 mb-2">
                Aucun film trouvé
              </h3>
              <p className="text-gray-500">
                {hasFilters
                  ? "Essayez d'autres critères de recherche."
                  : "La bibliothèque est vide."}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
